import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Radio,
  RadioGroup,
  SelectionControlLabel,
  Skeleton,
  SkeletonLine,
  Typography
} from '@one-thd/sui-atomic-components';
import { SelectableRadioCard } from './SelectableRadioCard';
import { getQuoteRadioValue, getQuotesPageSize } from '../../utils/helpers';
import {
  NEW_QUOTE_OPTION, QUOTE_HAS_APPLIANCE_MESSAGE, QUOTE_HAS_GM_MESSAGE, QUOTE_SELECTION_RADIO_GROUP_NAME
} from './Constants';

const Quotes = ({
  quoteList,
  selectQuote,
  selectedQuote,
  showLoadMore,
  quotesPageSize,
  setQuotesPageSize,
  showNewATQUI,
  isApplianceItem,
  isQuoteListLoading,
  deselectQuote
}) => {

  const pageSizeStep = getQuotesPageSize(showNewATQUI);
  const containerRef = useRef(null);
  // Set when the shopper presses Show More / Show Less, so focus can be handed to the list if the
  // button then legitimately disappears (e.g. the expanded page turned out to hold nothing new).
  const restoreFocusRef = useRef(false);
  const showMoreLessSelector = '[data-testid="show-more-button"], [data-testid="show-less-button"]';

  useEffect(() => {
    if (!restoreFocusRef.current || isQuoteListLoading || !containerRef.current) return;
    const button = containerRef.current.querySelector(showMoreLessSelector);
    if (button) {
      // Focus is still on the button - keep waiting; if it moved elsewhere, the shopper took over.
      if (document.activeElement !== button) restoreFocusRef.current = false;
      return;
    }
    restoreFocusRef.current = false;
    const radios = containerRef.current.querySelectorAll('input[type="radio"]:not(:disabled)');
    if (radios.length) radios[radios.length - 1].focus();
  });

  const handleLoadMore = () => {
    if (isQuoteListLoading) return;
    restoreFocusRef.current = true;
    setQuotesPageSize(quotesPageSize + pageSizeStep);
  };

  const handleShowLess = () => {
    if (isQuoteListLoading) return;
    restoreFocusRef.current = true;
    // The selected quote may only exist in the page being collapsed away - once it's no longer
    // in the visible slice its radio can't reflect a "selected" state, so clear the selection
    // rather than leave the UI pointing at a quote the user can no longer see or interact with.
    // The "New Quote" option isn't part of quoteList, so it must never be treated as hidden.
    const isSelectedQuoteStillVisible = selectedQuote.quoteId === NEW_QUOTE_OPTION
      || !selectedQuote.quoteId
      || quoteList
        .slice(0, pageSizeStep)
        .some((quote) => quote.cartId === selectedQuote.quoteId);
    if (!isSelectedQuoteStillVisible) {
      deselectQuote();
    }
    setQuotesPageSize(pageSizeStep);
  };

  const renderQuoteCard = (quote, disabled) => (
    <SelectableRadioCard
      key={quote.cartId}
      value={getQuoteRadioValue(quote)}
      selected={selectedQuote.quoteId === quote.cartId}
      disabled={disabled}
      label={quote.name}
    />
  );

  const renderNewQuoteCards = () => {
    const visibleQuoteList = quoteList.slice(0, quotesPageSize);
    const enabledQuotes = visibleQuoteList.filter(
      (quote) => Boolean(quote.hasApplianceItem) === isApplianceItem
    );
    const disabledQuotes = visibleQuoteList.filter(
      (quote) => Boolean(quote.hasApplianceItem) !== isApplianceItem
    );
    const disabledMessage = isApplianceItem ? QUOTE_HAS_GM_MESSAGE : QUOTE_HAS_APPLIANCE_MESSAGE;

    const cards = enabledQuotes.map((quote) => renderQuoteCard(quote, false));

    if (disabledQuotes.length > 0) {
      cards.push(
        <div key="appliance-divider" className="sui-mb-4 sui-border-b-1 sui-border-primary sui-border-solid" />,
        <div key="appliance-message" className="sui-pb-3">
          <Typography variant="body-sm">{disabledMessage}</Typography>
        </div>,
        ...disabledQuotes.map((quote) => renderQuoteCard(quote, true))
      );
    }

    return cards;
  };

  // One button whose label/handler/test id switch, rendered in the same tree position for the whole
  // Show More -> Show Less cycle. Swapping it for the skeleton (or returning null for a render)
  // would unmount the focused button and drop keyboard focus to <body>.
  const renderShowMoreOrLess = () => {
    let mode = null;
    if (showLoadMore) {
      mode = 'more';
    } else if (quotesPageSize > pageSizeStep && quoteList.length > pageSizeStep) {
      mode = 'less';
    } else if (quoteList.length > quotesPageSize) {
      // Right after Show Less, showLoadMore is only corrected by an effect one render later; the
      // list still holds more than the collapsed page, so keep offering Show More meanwhile.
      mode = 'more';
    }
    if (!mode) return null;

    const isLess = mode === 'less';
    return (
      <Button
        variant="text"
        onClick={isLess ? handleShowLess : handleLoadMore}
        data-testid={isLess ? 'show-less-button' : 'show-more-button'}
        className="sui-w-full sui-flex sui-justify-center sui-underline sui-text-base"
      >
        {isLess ? 'Show Less' : 'Show More'}
      </Button>
    );
  };

  const loadingSkeleton = (
    <Skeleton data-testid="quote-list-loading-skeleton" className="sui-mb-4">
      <SkeletonLine numberOfLines={3} />
    </Skeleton>
  );

  return (
    <div ref={containerRef} className={!showNewATQUI ? 'sui-pt-4' : undefined}>
      {showNewATQUI ? renderNewQuoteCards() : (
        // Legacy UI: this list is its own radio group. In the new UI the parent
        // (AddToQuoteFormDrawer) owns a single RadioGroup around the New Quote card and these cards.
        <RadioGroup aria-labelledby="quote-names" name={QUOTE_SELECTION_RADIO_GROUP_NAME}>
          {quoteList.map((quote) => (
            <SelectionControlLabel
              key={quote.cartId}
              id={quote.cartId}
              onChange={selectQuote}
              value={getQuoteRadioValue(quote)}
              label={quote.name}
              checked={selectedQuote.quoteId === quote.cartId}
              padding="true"
            >
              <Radio />
            </SelectionControlLabel>
          ))}
        </RadioGroup>
      )}
      {showNewATQUI && (
        <>
          {renderShowMoreOrLess()}
          {isQuoteListLoading && loadingSkeleton}
        </>
      )}
      {!showNewATQUI && isQuoteListLoading && loadingSkeleton}
      {!showNewATQUI && !isQuoteListLoading && showLoadMore && (
        <Button
          variant="text"
          onClick={handleLoadMore}
          data-testid="load-more-button"
        >
          Load More...
        </Button>
      )}
    </div>
  );
};

Quotes.propTypes = {
  quoteList: PropTypes.arrayOf(
    PropTypes.shape({
      quoteId: PropTypes.string,
      quoteName: PropTypes.string,
      userId: PropTypes.string,
      hasApplianceItem: PropTypes.bool
    })
  ).isRequired,
  selectedQuote: PropTypes.shape({
    quoteId: PropTypes.string,
    quoteName: PropTypes.string,
    userId: PropTypes.string
  }).isRequired,
  showLoadMore: PropTypes.bool.isRequired,
  setQuotesPageSize: PropTypes.func.isRequired,
  quotesPageSize: PropTypes.number.isRequired,
  selectQuote: PropTypes.func.isRequired,
  showNewATQUI: PropTypes.bool,
  isApplianceItem: PropTypes.bool,
  isQuoteListLoading: PropTypes.bool,
  deselectQuote: PropTypes.func
};

Quotes.defaultProps = {
  deselectQuote: () => {},
  showNewATQUI: false,
  isApplianceItem: false,
  isQuoteListLoading: false
};

Quotes.displayName = 'Quotes';

export { Quotes };
